import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function useCountUp(target, ms = 650) {
  const [v, setV] = useState(0);

  useEffect(() => {
    const to = Number.isFinite(target) ? target : 0;
    const from = v;
    if (to === from) return;

    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = clamp((t - start) / ms, 0, 1);
      // Ease-out cubic.
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, ms]);

  return v;
}

function statusCounts(rows) {
  const map = {};
  (rows || []).forEach(r => {
    const k = (r?.status || 'Unknown').toString().trim() || 'Unknown';
    map[k] = (map[k] || 0) + 1;
  });
  return map;
}

function sparkPoints(values) {
  const v = (values || []).map(x => (Number.isFinite(x) ? x : 0));
  const n = v.length || 1;
  const min = Math.min(...v, 0);
  const max = Math.max(...v, 1);
  const span = Math.max(1, max - min);
  const w = 86;
  const h = 26;
  const pad = 2;
  return v.map((x, i) => {
    const px = pad + (i * (w - pad * 2)) / Math.max(1, n - 1);
    const py = pad + (1 - (x - min) / span) * (h - pad * 2);
    return [px, py];
  });
}

function Spark({ values }) {
  const pts = sparkPoints(values);
  const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`).join(' ');
  const fill = `${d} L 84 24 L 2 24 Z`;
  return (
    <svg className="spark" viewBox="0 0 86 26" aria-hidden="true">
      <path className="fill" d={fill} />
      <path d={d} />
    </svg>
  );
}

function monthKey(isoDate) {
  if (!isoDate) return null;
  // Expecting YYYY-MM-DD
  return String(isoDate).slice(0, 7);
}

function buildLastMonths(n = 6) {
  const d = new Date();
  d.setDate(1);
  const months = [];
  for (let i = n - 1; i >= 0; i--) {
    const x = new Date(d);
    x.setMonth(x.getMonth() - i);
    months.push(x.toISOString().slice(0, 7));
  }
  return months;
}

function trendMonthYearParts(ym) {
  if (!ym || typeof ym !== 'string') return { month: '—', year: '' };
  const [ys, ms] = ym.split('-');
  if (!ys || !ms) return { month: ym, year: '' };
  const yi = parseInt(ys, 10);
  const mi = parseInt(ms, 10) - 1;
  if (Number.isNaN(yi) || Number.isNaN(mi)) return { month: ym, year: ys || '' };
  const d = new Date(yi, mi, 1);
  const month = d.toLocaleString('en', { month: 'short' });
  return { month, year: String(yi) };
}

function trendMonthLabelLong(ym) {
  if (!ym || typeof ym !== 'string') return '—';
  const [ys, ms] = ym.split('-');
  if (!ys || !ms) return String(ym);
  const yi = parseInt(ys, 10);
  const mi = parseInt(ms, 10) - 1;
  if (Number.isNaN(yi) || Number.isNaN(mi)) return String(ym);
  return new Date(yi, mi, 1).toLocaleString('en', { month: 'long', year: 'numeric' });
}

function LineChart({ months, values, stroke = 'rgba(15, 139, 141, 0.95)' }) {
  const [lineTip, setLineTip] = useState(null);
  const [lineHover, setLineHover] = useState(null);
  const w = 520;
  const h = 200;
  const padX = 16;
  const padTop = 14;
  const padBottom = 36;
  const chartH = h - padTop - padBottom;
  const v = (values || []).map(x => (Number.isFinite(x) ? x : 0));
  const max = Math.max(1, ...v);
  const n = Math.max(1, v.length);
  const pts = v.map((x, i) => {
    const px = padX + (i * (w - padX * 2)) / Math.max(1, n - 1);
    const py = padTop + (1 - x / max) * chartH;
    return [px, py];
  });

  const keys = months || [];

  const moveLineTip = (e, payload) => {
    const wrap = e.currentTarget.closest('.dash-line-wrap');
    const rect = wrap?.getBoundingClientRect();
    if (!rect) return;
    setLineTip({
      ...payload,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const smoothPath = (points, tension = 0.22) => {
    if (!points.length) return '';
    if (points.length === 1) {
      const [x, y] = points[0];
      return `M ${x.toFixed(2)} ${y.toFixed(2)}`;
    }
    let d0 = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i - 1] || points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[i + 2] || p2;

      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;

      d0 += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
    }
    return d0;
  };

  const d = smoothPath(pts);
  const fill = `${d} L ${w - padX} ${padTop + chartH} L ${padX} ${padTop + chartH} Z`;

  return (
    <div className="dash-line-wrap">
      <svg className="dash-line" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Invoice totals by month">
        <defs>
          <linearGradient id="dashLineFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path className="dash-line-fill" d={fill} fill="url(#dashLineFill)" pointerEvents="none" />
        <path className="dash-line-path" d={d} stroke={stroke} pointerEvents="none" />
        {pts.map(([x, y], idx) => {
          const ym = typeof keys[idx] === 'string' ? keys[idx] : '';
          const val = v[idx];
          return (
            <circle
              key={`hit-${idx}`}
              className="dash-line-hit"
              cx={x}
              cy={y}
              r={14}
              fill="transparent"
              onMouseEnter={(e) => {
                setLineHover(idx);
                moveLineTip(e, { monthLabel: trendMonthLabelLong(ym), total: val });
              }}
              onMouseMove={(e) => moveLineTip(e, { monthLabel: trendMonthLabelLong(ym), total: val })}
              onMouseLeave={() => {
                setLineTip(null);
                setLineHover(null);
              }}
            />
          );
        })}
        {pts.map(([x, y], idx) => (
          <circle
            key={`dot-${idx}`}
            className="dash-line-dot"
            cx={x}
            cy={y}
            r={lineHover === idx ? 5 : 3.2}
            fill={stroke}
            pointerEvents="none"
          />
        ))}
        <g className="dash-line-axis" aria-hidden="true" pointerEvents="none">
          {v.map((_, i) => {
            const cx = padX + (i * (w - padX * 2)) / Math.max(1, n - 1);
            const ym = typeof keys[i] === 'string' ? keys[i] : '';
            const { month, year } = trendMonthYearParts(ym);
            return (
              <text key={`axis-${i}`} textAnchor="middle" className="dash-line-axis-group">
                <tspan className="dash-line-axis-month" x={cx} y={h - 24}>{month}</tspan>
                <tspan className="dash-line-axis-year" x={cx} y={h - 8}>{year}</tspan>
              </text>
            );
          })}
        </g>
      </svg>
      {lineTip ? (
        <div
          className="dash-tooltip"
          style={{ left: lineTip.x, top: lineTip.y }}
          role="tooltip"
        >
          <div className="dash-tooltip-title">{lineTip.monthLabel}</div>
          <div className="dash-tooltip-row">
            <span>Total billed</span>
            <strong className="tabular-nums">{Number(lineTip.total || 0).toLocaleString()}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TrendBarChart({ months, values }) {
  const [barTip, setBarTip] = useState(null);
  const [barHover, setBarHover] = useState(null);
  const w = 520;
  const h = 240;
  const pad = { l: 40, r: 20, t: 20, b: 46 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const v = (values || []).map(x => (Number.isFinite(x) ? x : 0));
  const n = Math.max(1, v.length);
  const gap = 10;
  const bw = n ? (innerW - gap * (n - 1)) / n : innerW;
  const max = Math.max(1, ...v);
  const keys = months || [];

  const moveBarTip = (e, payload) => {
    const wrap = e.currentTarget.closest('.dash-bars-wrap');
    const rect = wrap?.getBoundingClientRect();
    if (!rect) return;
    setBarTip({
      ...payload,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <div className="dash-bars-wrap">
      <svg className="dash-bars" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Invoice totals as bars">
        {v.map((val, i) => {
          const x = pad.l + i * (bw + gap);
          const ym = typeof keys[i] === 'string' ? keys[i] : '';
          return (
            <rect
              key={`hit-${i}`}
              className="dash-bar-hit"
              x={x}
              y={pad.t}
              width={bw}
              height={innerH}
              fill="transparent"
              onMouseEnter={(e) => {
                setBarHover(i);
                moveBarTip(e, { monthLabel: trendMonthLabelLong(ym), total: val });
              }}
              onMouseMove={(e) => moveBarTip(e, { monthLabel: trendMonthLabelLong(ym), total: val })}
              onMouseLeave={() => {
                setBarTip(null);
                setBarHover(null);
              }}
            />
          );
        })}
        {v.map((val, i) => {
          const bh = (val / max) * innerH;
          const x = pad.l + i * (bw + gap);
          const y = pad.t + innerH - bh;
          return (
            <rect
              key={`bar-${i}`}
              className={`dash-bar-rect${barHover === i ? ' dash-bar-rect--hover' : ''}`}
              x={x}
              y={y}
              width={bw}
              height={Math.max(bh, 2)}
              rx={4}
              fill="rgba(15, 139, 141, 0.88)"
              pointerEvents="none"
            />
          );
        })}
        {v.map((_, i) => {
          const cx = pad.l + i * (bw + gap) + bw / 2;
          const ym = typeof keys[i] === 'string' ? keys[i] : '';
          const { month, year } = trendMonthYearParts(ym);
          return (
            <text key={`lbl-${i}`} textAnchor="middle" className="dash-bar-label-group" pointerEvents="none">
              <tspan className="dash-bar-label-month" x={cx} y={h - 26}>{month}</tspan>
              <tspan className="dash-bar-label-year" x={cx} y={h - 10}>{year}</tspan>
            </text>
          );
        })}
      </svg>
      {barTip ? (
        <div
          className="dash-tooltip"
          style={{ left: barTip.x, top: barTip.y }}
          role="tooltip"
        >
          <div className="dash-tooltip-title">{barTip.monthLabel}</div>
          <div className="dash-tooltip-row">
            <span>Total billed</span>
            <strong className="tabular-nums">{Number(barTip.total || 0).toLocaleString()}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const APPROVAL_ENTITY_TYPES = ['MSA', 'SOW', 'Engagement', 'Invoice', 'Timesheet'];

function formatUserAddedAt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function dashUserRoleBadgeClass(role) {
  if (role === 'Admin') return 'badge-approved';
  if (role === 'HR Manager') return 'badge-submitted';
  return 'badge-pending';
}

function IconRecentUsersTitle() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconThUser() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
      <path d="M4 20a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconThId() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 7h6M4 12h6M4 17h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <rect x="14" y="5" width="6" height="14" rx="1" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconThRole() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 3l2 4 4.5.5-3.25 3.25L16 15l-4-2.25L8 15l.75-4.25L5.5 7.5 10 7l2-4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

function IconThStatus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z" stroke="currentColor" strokeWidth="2" />
      <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconThClock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconRoleGlyph({ role }) {
  const s = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true };
  if (role === 'Admin') {
    return (
      <svg {...s}>
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    );
  }
  if (role === 'HR Manager') {
    return (
      <svg {...s}>
        <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M8 7V5a4 4 0 0 1 8 0v2" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  return (
    <svg {...s}>
      <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
      <path d="M6 20a6 6 0 0 1 12 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconActive() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconInactive() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

export default function Dashboard() {
  const { user, canView } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingReady, setPendingReady] = useState(false);
  const [trendView, setTrendView] = useState('line');
  const [stats, setStats] = useState({ customers: 0, msas: 0, sows: 0, invoices: 0 });
  const [allCustomers, setAllCustomers] = useState([]);
  const [allMsas, setAllMsas] = useState([]);
  const [allSows, setAllSows] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [recentMsa, setRecentMsa] = useState([]);
  const [recentInv, setRecentInv] = useState([]);
  const [msaStatus, setMsaStatus] = useState({});
  const [invStatus, setInvStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [sowTip, setSowTip] = useState(null);
  const [msaBarTip, setMsaBarTip] = useState(null);
  const [invDonutTip, setInvDonutTip] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentUsersReady, setRecentUsersReady] = useState(false);

  useEffect(() => {
    if (user?.role !== 'Admin') {
      setRecentUsers([]);
      setRecentUsersReady(false);
      return undefined;
    }
    let cancelled = false;
    setRecentUsersReady(false);
    api
      .get('/auth/users')
      .then(list => {
        if (cancelled) return;
        const rows = [...(list || [])].sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (tb !== ta) return tb - ta;
          return (b.id || 0) - (a.id || 0);
        });
        setRecentUsers(rows.slice(0, 10));
      })
      .catch(() => {
        if (!cancelled) setRecentUsers([]);
      })
      .finally(() => {
        if (!cancelled) setRecentUsersReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/approvals/pending')
      .then((p) => {
        if (cancelled) return;
        setPendingApprovals(p || []);
      })
      .catch(() => {
        if (cancelled) return;
        setPendingApprovals([]);
      })
      .finally(() => {
        if (!cancelled) setPendingReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([api.get('/customers'), api.get('/msa'), api.get('/sow'), api.get('/invoices')])
      .then(([c, m, s, i]) => {
        if (!mounted) return;
        setAllCustomers(c || []);
        setAllMsas(m || []);
        setAllSows(s || []);
        setAllInvoices(i || []);
        setStats({ customers: c.length, msas: m.length, sows: s.length, invoices: i.length });
        setRecentMsa((m || []).slice(0, 6));
        setRecentInv((i || []).slice(0, 6));
        setMsaStatus(statusCounts(m));
        setInvStatus(statusCounts(i));
      })
      .finally(() => mounted && setLoading(false));

    return () => { mounted = false; };
  }, []);

  const pendingByType = useMemo(() => {
    const counts = {};
    APPROVAL_ENTITY_TYPES.forEach(t => {
      counts[t] = 0;
    });
    (pendingApprovals || []).forEach(p => {
      const k = p.entity_type;
      if (k && counts[k] !== undefined) counts[k] += 1;
    });
    return counts;
  }, [pendingApprovals]);

  const customersUp = useCountUp(stats.customers);
  const msasUp = useCountUp(stats.msas);
  const sowsUp = useCountUp(stats.sows);
  const invoicesUp = useCountUp(stats.invoices);

  const msaBars = useMemo(() => {
    const entries = Object.entries(msaStatus || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const max = Math.max(1, ...entries.map(([, n]) => n));
    return entries.map(([name, n]) => ({ name, n, p: Math.round((n / max) * 100) }));
  }, [msaStatus]);

  const msaStatusGrandTotal = useMemo(
    () => Math.max(1, Object.values(msaStatus || {}).reduce((sum, x) => sum + Number(x || 0), 0)),
    [msaStatus]
  );

  const insight = useMemo(() => {
    const activeMsas = (msaStatus?.Active || 0) + (msaStatus?.Approved || 0);
    const expiredMsas = (msaStatus?.Expired || 0) + (msaStatus?.Terminated || 0);

    const sowStatus = statusCounts(allSows);
    const activeSows = (sowStatus?.Active || 0);

    const invPaid = (invStatus?.Paid || 0);
    const invOverdue = (invStatus?.Overdue || 0);
    const invCancelled = (invStatus?.Cancelled || 0);

    const highRiskCustomers = (allCustomers || []).filter(x => (x?.risk_category || '').toString() === 'High').length;
    const suspendedCustomers = (allCustomers || []).filter(x => (x?.status || '').toString() === 'Suspended').length;

    const currencyMap = {};
    (allInvoices || []).forEach(r => {
      const k = (r?.invoice_currency || r?.invoicing_currency || r?.currency || '—').toString();
      currencyMap[k] = (currencyMap[k] || 0) + 1;
    });
    const topCurrency = Object.entries(currencyMap).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

    return {
      activeMsas,
      expiredMsas,
      activeSows,
      invPaid,
      invOverdue,
      invCancelled,
      highRiskCustomers,
      suspendedCustomers,
      topCurrency,
    };
  }, [allCustomers, allSows, allInvoices, msaStatus, invStatus]);

  const activeMsasUp = useCountUp(insight.activeMsas);
  const activeSowsUp = useCountUp(insight.activeSows);
  const overdueUp = useCountUp(insight.invOverdue);
  const highRiskUp = useCountUp(insight.highRiskCustomers);

  const invoiceDonut = useMemo(() => {
    const palette = [
      'rgba(15, 139, 141, 0.95)',
      'rgba(11, 42, 74, 0.92)',
      'rgba(56, 161, 105, 0.95)',
      'rgba(214, 158, 46, 0.95)',
      'rgba(229, 62, 62, 0.95)',
      'rgba(113, 128, 150, 0.9)',
    ];
    const entries = Object.entries(invStatus || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const total = Math.max(1, entries.reduce((acc, [, n]) => acc + n, 0));
    const r = 46;
    const c = 2 * Math.PI * r;
    let off = 0;
    return entries.map(([name, n], idx) => {
      const dash = (n / total) * c;
      const seg = {
        name,
        n,
        c: palette[idx % palette.length],
        dash,
        off: -off,
        pct: Math.round((n / total) * 100),
      };
      off += dash;
      return seg;
    });
  }, [invStatus]);

  const invoiceSpark = useMemo(() => {
    const totals = (recentInv || []).slice().reverse().map(x => Number(x?.total || 0));
    return totals.length ? totals : [0, 0, 0, 0, 0];
  }, [recentInv]);

  const invoiceTrend = useMemo(() => {
    const months = buildLastMonths(6);
    const totalsByMonth = {};
    (allInvoices || []).forEach(inv => {
      const mk = monthKey(inv?.invoice_date);
      if (!mk) return;
      totalsByMonth[mk] = (totalsByMonth[mk] || 0) + Number(inv?.total || 0);
    });
    return {
      months,
      values: months.map(m => Number(totalsByMonth[m] || 0)),
      labels: months.map(m => m.slice(5)),
    };
  }, [allInvoices]);

  /** Sum of invoice totals per status (distinct from monthly trend + count donut). */
  const invoicePipeline = useMemo(() => {
    const map = {};
    (allInvoices || []).forEach(inv => {
      const st = (inv?.status || 'Unknown').toString().trim() || 'Unknown';
      map[st] = (map[st] || 0) + Number(inv?.total || 0);
    });
    const rows = Object.entries(map)
      .map(([name, amt]) => ({ name, amt }))
      .sort((a, b) => b.amt - a.amt)
      .slice(0, 6);
    const grand = Math.max(1, rows.reduce((a, r) => a + r.amt, 0));
    const maxAmt = Math.max(1, ...rows.map(r => r.amt));
    return rows.map(r => ({
      ...r,
      p: Math.round((r.amt / maxAmt) * 100),
      share: Math.round((r.amt / grand) * 100),
    }));
  }, [allInvoices]);

  const topCustomers = useMemo(() => {
    const map = {};
    (allInvoices || []).forEach(inv => {
      const name = (inv?.client_name || inv?.legal_name || inv?.customer_id || '—').toString();
      map[name] = (map[name] || 0) + Number(inv?.total || 0);
    });
    const rows = Object.entries(map)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);
    const max = Math.max(1, ...rows.map(r => r.total));
    return rows.map(r => ({ ...r, p: Math.round((r.total / max) * 100) }));
  }, [allInvoices]);

  const sowModel = useMemo(() => {
    const map = {};
    (allSows || []).forEach(s => {
      const k = (s?.billing_model || '—').toString();
      map[k] = (map[k] || 0) + 1;
    });
    const rows = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const total = Math.max(1, rows.reduce((acc, [, n]) => acc + n, 0));
    return rows.map(([name, n], idx) => ({ name, n, pct: Math.round((n / total) * 100), idx }));
  }, [allSows]);

  const sowDonut = useMemo(() => {
    const palette = [
      'rgba(15, 139, 141, 0.95)',
      'rgba(11, 42, 74, 0.92)',
      'rgba(56, 161, 105, 0.95)',
      'rgba(214, 158, 46, 0.95)',
      'rgba(229, 62, 62, 0.95)',
      'rgba(113, 128, 150, 0.9)',
    ];
    const entries = (sowModel || []).slice(0, 6);
    const total = Math.max(1, entries.reduce((acc, r) => acc + (r.n || 0), 0));
    const r = 34;
    const c = 2 * Math.PI * r;
    let off = 0;
    return entries.map((row, idx) => {
      const dash = ((row.n || 0) / total) * c;
      const seg = { ...row, c: palette[idx % palette.length], dash, off: -off, total };
      off += dash;
      return seg;
    });
  }, [sowModel]);

  const topSowModel = useMemo(() => sowModel?.[0]?.name || '—', [sowModel]);

  const msaSpark = useMemo(() => {
    const statusTrend = ['Draft', 'Submitted', 'Approved', 'Active', 'Expired', 'Terminated']
      .map(k => msaStatus?.[k] || 0);
    return statusTrend.some(Boolean) ? statusTrend : [0, 0, 0, 0, 0, 0];
  }, [msaStatus]);

  const moveMsaBarTip = (e, payload) => {
    const wrap = e.currentTarget.closest('.msa-hbars-wrap');
    const rect = wrap?.getBoundingClientRect();
    if (!rect) return;
    setMsaBarTip({ ...payload, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const moveInvDonutTip = (e, payload) => {
    const wrap = e.currentTarget.closest('.inv-donut-outer');
    const rect = wrap?.getBoundingClientRect();
    if (!rect) return;
    setInvDonutTip({ ...payload, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <>
      <div className="page-header">
        <h1>Dashboard</h1>
      </div>
      <div className="page-content">
        <div className="dash-grid">
          <div className="dash-welcome" role="status" aria-live="polite">
            <p className="dash-welcome-line">
              <span className="dash-welcome-greeting">Welcome,</span>
              <span className="dash-welcome-name">
                {user?.full_name || user?.username || 'User'}
              </span>
            </p>
            {user?.role ? (
              <p className="dash-welcome-role">{user.role}</p>
            ) : null}
          </div>

          {canView('approvals') ? (
            <section className="dash-pending-strip" aria-label="Pending approvals at a glance">
              <div className="dash-pending-strip-main">
                <div className="dash-pending-total">
                  <span className="dash-pending-total-icon" aria-hidden>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span className="dash-pending-total-text">
                    <span className="dash-pending-total-label">Pending approval</span>
                    <span className="dash-pending-total-value tabular-nums">
                      {pendingReady ? pendingApprovals.length.toLocaleString() : '—'}
                    </span>
                  </span>
                </div>
                <div className="dash-pending-divider" aria-hidden />
                <div className="dash-pending-type-row" role="list">
                  {APPROVAL_ENTITY_TYPES.map(t => {
                    const n = pendingByType[t] || 0;
                    return (
                      <span key={t} className={`dash-pending-pill${n > 0 ? ' dash-pending-pill--active' : ''}`} role="listitem">
                        <span className="dash-pending-pill-name">{t}</span>
                        <span className="dash-pending-pill-n tabular-nums">{pendingReady ? n.toLocaleString() : '—'}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
              <Link
                to="/approvals"
                className="btn btn-primary btn-sm dash-pending-cta"
                aria-label="Open pending approvals queue"
              >
                Open approvals
              </Link>
            </section>
          ) : null}

          <div className="dash-kpis">
            <Link to="/customers" className="kpi-card kpi-link" aria-label="Go to Customers">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{customersUp.toLocaleString()}</div>
                  <div className="kpi-label">Customers</div>
                </div>
                <div className="kpi-icon teal" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M2 20c0-2.76 2.24-5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M22 20c0-2.76-2.24-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M7 20c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">Master records</div>
                <Spark values={[0, customersUp * 0.35, customersUp * 0.6, customersUp * 0.82, customersUp]} />
              </div>
            </Link>

            <Link to="/msa" className="kpi-card kpi-link" aria-label="Go to MSAs">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{msasUp.toLocaleString()}</div>
                  <div className="kpi-label">MSA Agreements</div>
                </div>
                <div className="kpi-icon green" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M7 3h7l3 3v15a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M14 3v4h4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 12h8M8 16h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">Lifecycle view</div>
                <Spark values={msaSpark} />
              </div>
            </Link>

            <Link to="/sow" className="kpi-card kpi-link" aria-label="Go to SOWs">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{sowsUp.toLocaleString()}</div>
                  <div className="kpi-label">SOW Agreements</div>
                </div>
                <div className="kpi-icon amber" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 6h16M4 12h10M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M18 10v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M15.5 15.5 18 18l2.5-2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">Active work scopes</div>
                <Spark values={[0, sowsUp * 0.25, sowsUp * 0.45, sowsUp * 0.7, sowsUp]} />
              </div>
            </Link>

            <Link to="/invoices" className="kpi-card kpi-link" aria-label="Go to Billing">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{invoicesUp.toLocaleString()}</div>
                  <div className="kpi-label">Invoices</div>
                </div>
                <div className="kpi-icon red" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                    <path d="M9 7h6M9 11h6M9 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">Billing activity</div>
                <Spark values={invoiceSpark} />
              </div>
            </Link>
          </div>

          <div className="dash-kpis dash-kpis-secondary" aria-label="Highlights">
            <Link to="/msa" className="kpi-card kpi-compact kpi-link" aria-label="Go to Active MSAs">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{loading ? '—' : activeMsasUp.toLocaleString()}</div>
                  <div className="kpi-label">Active / Approved MSAs</div>
                </div>
                <div className="kpi-icon green" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M9 11 11 13 15 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">{loading ? 'Refreshing…' : `${insight.expiredMsas.toLocaleString()} expired/terminated`}</div>
                <Spark values={[0, activeMsasUp * 0.3, activeMsasUp * 0.55, activeMsasUp * 0.78, activeMsasUp]} />
              </div>
            </Link>

            <Link to="/sow" className="kpi-card kpi-compact kpi-link" aria-label="Go to Active SOWs">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{loading ? '—' : activeSowsUp.toLocaleString()}</div>
                  <div className="kpi-label">Active SOWs</div>
                </div>
                <div className="kpi-icon amber" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M4 6h16M4 12h10M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M18 10v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">{loading ? 'Refreshing…' : 'Time-bound deliverables'}</div>
                <Spark values={[0, activeSowsUp * 0.2, activeSowsUp * 0.44, activeSowsUp * 0.71, activeSowsUp]} />
              </div>
            </Link>

            <Link to="/invoices" className="kpi-card kpi-compact kpi-link" aria-label="Go to Overdue invoices">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{loading ? '—' : overdueUp.toLocaleString()}</div>
                  <div className="kpi-label">Overdue</div>
                </div>
                <div className="kpi-icon red" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 8v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">{loading ? 'Refreshing…' : `${insight.invPaid.toLocaleString()} paid · ${insight.invCancelled.toLocaleString()} cancelled`}</div>
                <Spark values={invoiceSpark} />
              </div>
            </Link>

            <Link to="/customers" className="kpi-card kpi-compact kpi-link" aria-label="Go to High-risk customers">
              <div className="kpi-top">
                <div>
                  <div className="kpi-value">{loading ? '—' : highRiskUp.toLocaleString()}</div>
                  <div className="kpi-label">High-Risk Customers</div>
                </div>
                <div className="kpi-icon teal" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 17h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              <div className="kpi-sub">
                <div className="hint">{loading ? 'Refreshing…' : `${insight.suspendedCustomers.toLocaleString()} suspended · Top currency ${insight.topCurrency}`}</div>
                <Spark values={[0, highRiskUp * 0.33, highRiskUp * 0.45, highRiskUp * 0.66, highRiskUp]} />
              </div>
            </Link>
          </div>

          <div className="dash-charts">
            <div className="dash-card chart-msas">
              <div className="dash-card-head">
                <div className="dash-card-title">MSA Status Mix</div>
                <div className="dash-card-meta">{Object.keys(msaStatus || {}).length ? 'Top statuses' : 'No status data yet'}</div>
              </div>
              <div className="msa-hbars-wrap">
                <div className="hbars" aria-label="MSA status mix">
                  {(msaBars.length ? msaBars : [{ name: 'No data', n: 0, p: 0 }]).map(r => {
                    const shareAll = msaBars.length ? Math.round((r.n / msaStatusGrandTotal) * 100) : 0;
                    return (
                      <div
                        className="hbar"
                        key={r.name}
                        onMouseEnter={(e) =>
                          msaBars.length
                            ? moveMsaBarTip(e, {
                                title: r.name,
                                n: r.n,
                                p: r.p,
                                shareAll,
                              })
                            : undefined
                        }
                        onMouseMove={(e) =>
                          msaBars.length
                            ? moveMsaBarTip(e, {
                                title: r.name,
                                n: r.n,
                                p: r.p,
                                shareAll,
                              })
                            : undefined
                        }
                        onMouseLeave={() => setMsaBarTip(null)}
                      >
                        <div className="name">{r.name}</div>
                        <div className="track">
                          <div className="bar" style={{ '--p': r.p }} />
                        </div>
                        <div className="value">{r.n.toLocaleString()}</div>
                      </div>
                    );
                  })}
                </div>
                {msaBarTip ? (
                  <div
                    className="dash-tooltip"
                    style={{ left: msaBarTip.x, top: msaBarTip.y }}
                    role="tooltip"
                  >
                    <div className="dash-tooltip-title">{msaBarTip.title}</div>
                    <div className="dash-tooltip-row">
                      <span>MSAs</span>
                      <strong className="tabular-nums">{(msaBarTip.n || 0).toLocaleString()}</strong>
                    </div>
                    <div className="dash-tooltip-row">
                      <span>Share of all MSAs</span>
                      <strong className="tabular-nums">{msaBarTip.shareAll}%</strong>
                    </div>
                    <div className="dash-tooltip-row">
                      <span>Bar vs top status</span>
                      <strong className="tabular-nums">{msaBarTip.p}%</strong>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="dash-card chart-inv">
              <div className="dash-card-head">
                <div className="dash-card-title">Invoice Status</div>
                <div className="dash-card-meta">{Object.keys(invStatus || {}).length ? 'Distribution' : 'No invoices yet'}</div>
              </div>

              <div className="inv-donut-outer">
                <div className="donut-wrap">
                  <svg className="donut" viewBox="0 0 120 120" aria-hidden="true">
                    <circle className="bg" cx="60" cy="60" r="46" />
                    {invoiceDonut.map((s) => (
                      <circle
                        key={s.name}
                        className="seg"
                        cx="60"
                        cy="60"
                        r="46"
                        style={{
                          stroke: s.c,
                          '--dash': `${s.dash}px`,
                          '--off': `${s.off}px`,
                        }}
                        onMouseEnter={(e) =>
                          moveInvDonutTip(e, { title: s.name, n: s.n, pct: s.pct })
                        }
                        onMouseMove={(e) =>
                          moveInvDonutTip(e, { title: s.name, n: s.n, pct: s.pct })
                        }
                        onMouseLeave={() => setInvDonutTip(null)}
                      />
                    ))}
                  </svg>
                  <div
                    className={`legend${invoiceDonut.length ? ' legend--has-data' : ''}`}
                    aria-label="Invoice status legend"
                  >
                    {(invoiceDonut.length ? invoiceDonut : [{ name: 'No data', n: 0, c: 'rgba(113,128,150,0.8)', pct: 0 }]).map(s => (
                      <div
                        className="legend-item"
                        key={s.name}
                        onMouseEnter={(e) =>
                          invoiceDonut.length
                            ? moveInvDonutTip(e, { title: s.name, n: s.n, pct: s.pct })
                            : undefined
                        }
                        onMouseMove={(e) =>
                          invoiceDonut.length
                            ? moveInvDonutTip(e, { title: s.name, n: s.n, pct: s.pct })
                            : undefined
                        }
                        onMouseLeave={() => setInvDonutTip(null)}
                      >
                        <div className="left">
                          <span className="dot" style={{ '--c': s.c }} />
                          {s.name}
                        </div>
                        <div className="n">{(s.n || 0).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {invDonutTip ? (
                  <div
                    className="dash-tooltip"
                    style={{ left: invDonutTip.x, top: invDonutTip.y }}
                    role="tooltip"
                  >
                    <div className="dash-tooltip-title">{invDonutTip.title}</div>
                    <div className="dash-tooltip-row">
                      <span>Invoices</span>
                      <strong className="tabular-nums">{(invDonutTip.n || 0).toLocaleString()}</strong>
                    </div>
                    <div className="dash-tooltip-row">
                      <span>Share</span>
                      <strong className="tabular-nums">{invDonutTip.pct}%</strong>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="dash-card chart-line">
              <div className="dash-card-head trend-head">
                <div className="trend-head-text">
                  <div className="dash-card-title">Invoice Trend (Last 6 months)</div>
                  <div className="dash-card-meta">{loading ? 'Refreshing…' : 'Totals from existing invoices'}</div>
                </div>
                <div className="trend-view-toggle" role="toolbar" aria-label="Switch invoice trend view">
                  <button
                    type="button"
                    className={trendView === 'line' ? 'active' : ''}
                    onClick={() => setTrendView('line')}
                    title="Line chart"
                    aria-pressed={trendView === 'line'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 18V6M8 18v-5M12 18V9M16 18v-3M20 18v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={trendView === 'bars' ? 'active' : ''}
                    onClick={() => setTrendView('bars')}
                    title="Bar chart"
                    aria-pressed={trendView === 'bars'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M5 20V10M12 20V4M19 20v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={trendView === 'table' ? 'active' : ''}
                    onClick={() => setTrendView('table')}
                    title="Table"
                    aria-pressed={trendView === 'table'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M4 10h16M12 5v14" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
              {trendView === 'line' && (
                <LineChart months={invoiceTrend.months} values={invoiceTrend.values} />
              )}
              {trendView === 'bars' && (
                <TrendBarChart months={invoiceTrend.months} values={invoiceTrend.values} />
              )}
              {trendView === 'table' && (
                <div className="trend-table-wrap">
                  <table className="trend-table">
                    <thead>
                      <tr><th>Month</th><th className="text-right">Total</th></tr>
                    </thead>
                    <tbody>
                      {(invoiceTrend.months || []).map((m, i) => (
                        <tr key={m}>
                          <td>{m}</td>
                          <td className="text-right">{Number(invoiceTrend.values[i] || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="dash-card chart-top dash-topcx">
              <div className="dash-topcx-intro">
                <div className="dash-topcx-icon" aria-hidden>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" stroke="currentColor" strokeWidth="2" />
                    <path d="M8.5 14.5 4 22l4-1 4 1 4-1 4 1-4.5-7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="dash-topcx-intro-text">
                  <div className="dash-card-title dash-topcx-title">Revenue leaders</div>
                  <div className="dash-card-meta">
                    {loading ? 'Refreshing…' : 'Ranked by sum of invoice totals'}
                  </div>
                </div>
              </div>
              <ol className="dash-topcx-list" aria-label="Top customers by billed total">
                {(topCustomers.length ? topCustomers : [{ name: 'No invoice data yet', total: 0, p: 0 }]).map((r, idx) => (
                  <li
                    key={r.name}
                    className={`dash-topcx-row${idx === 0 ? ' dash-topcx-row--lead' : ''}${!topCustomers.length ? ' dash-topcx-row--empty' : ''}`}
                  >
                    <span className="dash-topcx-rank tabular-nums" aria-label={`Rank ${idx + 1}`}>
                      {topCustomers.length ? idx + 1 : '—'}
                    </span>
                    <div className="dash-topcx-main">
                      <div className="dash-topcx-name" title={r.name}>
                        {r.name}
                      </div>
                      {topCustomers.length > 0 ? (
                        <div className="dash-topcx-meter" aria-hidden>
                          <div className="dash-topcx-meter-fill" style={{ width: `${r.p}%` }} />
                        </div>
                      ) : null}
                    </div>
                    <div className="dash-topcx-amt">
                      <span className="dash-topcx-amt-num tabular-nums">
                        {topCustomers.length ? (r.total || 0).toLocaleString() : '—'}
                      </span>
                      {topCustomers.length > 0 && idx > 0 ? (
                        <span className="dash-topcx-amt-pct tabular-nums">{r.p}% of leader</span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="dash-card chart-sow">
              <div className="dash-card-head">
                <div className="dash-card-title">SOW Billing Models</div>
                <div className="dash-card-meta">{loading ? 'Refreshing…' : `Most used: ${topSowModel}`}</div>
              </div>
              <div className="sow-split" aria-label="SOW billing model breakdown">
                <div className="sow-donut">
                  <svg className="donut donut-sm" viewBox="0 0 120 120" aria-hidden="true">
                    <circle className="bg" cx="60" cy="60" r="34" />
                    {sowDonut.map((s) => (
                      <circle
                        key={s.name}
                        className="seg"
                        cx="60"
                        cy="60"
                        r="34"
                        style={{
                          stroke: s.c,
                          '--dash': `${s.dash}px`,
                          '--off': `${s.off}px`,
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                          if (!rect) return;
                          setSowTip({
                            name: s.name,
                            n: s.n || 0,
                            pct: s.pct || 0,
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                          });
                        }}
                        onMouseMove={(e) => {
                          const rect = e.currentTarget.ownerSVGElement?.getBoundingClientRect();
                          if (!rect) return;
                          setSowTip(t => t ? ({ ...t, x: e.clientX - rect.left, y: e.clientY - rect.top }) : t);
                        }}
                        onMouseLeave={() => setSowTip(null)}
                      />
                    ))}
                  </svg>
                  <div className="sow-donut-note">
                    <div className="sow-donut-big">{loading ? '—' : (allSows?.length || 0).toLocaleString()}</div>
                    <div className="sow-donut-sub">Total SOWs</div>
                  </div>
                  {sowTip && (
                    <div
                      className="dash-tooltip"
                      style={{ left: sowTip.x, top: sowTip.y }}
                      role="tooltip"
                    >
                      <div className="dash-tooltip-title">{sowTip.name}</div>
                      <div className="dash-tooltip-row">
                        <span>SOWs</span>
                        <strong>{sowTip.n.toLocaleString()}</strong>
                      </div>
                      <div className="dash-tooltip-row">
                        <span>Share</span>
                        <strong>{sowTip.pct}%</strong>
                      </div>
                    </div>
                  )}
                </div>

                <div className="dash-pills" aria-label="Model legend">
                  {(sowDonut.length ? sowDonut : [{ name: 'No data', n: 0, pct: 0, idx: 0, c: 'rgba(113,128,150,0.9)' }]).map(r => (
                    <div
                      className="dash-pill"
                      key={r.name}
                      style={{ '--i': r.idx, '--pill': r.c }}
                      onMouseEnter={() => setSowTip({ name: r.name, n: r.n || 0, pct: r.pct || 0, x: 60, y: 20 })}
                      onMouseLeave={() => setSowTip(null)}
                    >
                      <div className="dash-pill-name">
                        <span className="pill-dot" aria-hidden="true" style={{ background: r.c }} />
                        {r.name}
                      </div>
                      <div className="dash-pill-meta">
                        <span className="dash-pill-n">{(r.n || 0).toLocaleString()}</span>
                        <span className="dash-pill-pct">{(r.pct || 0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="dash-card chart-wavecard">
              <div className="dash-card-head">
                <div className="dash-card-title">Billing pipeline (value)</div>
                <div className="dash-card-meta">{loading ? 'Refreshing…' : 'Totals summed by invoice status'}</div>
              </div>
              <div className="pipeline-note">
                Amounts mix all invoicing currencies — compare relative split, not FX totals.
              </div>
              <div className="hbars pipeline-hbars" aria-label="Invoice value by status">
                {(invoicePipeline.length ? invoicePipeline : [{ name: 'No data', amt: 0, p: 0, share: 0 }]).map(r => (
                  <div className="hbar" key={r.name}>
                    <div className="name">{r.name}</div>
                    <div className="track">
                      <div className="bar bar-value" style={{ '--p': r.p }} />
                    </div>
                    <div className="value pipeline-value-col">
                      <span className="pipeline-amt">{Math.round(r.amt).toLocaleString()}</span>
                      <span className="pipeline-share">{r.share}%</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="wave-metrics" aria-label="Quick counts">
                <div className="wave-metric">
                  <div className="wave-k">Paid (count)</div>
                  <div className="wave-v">{loading ? '—' : insight.invPaid.toLocaleString()}</div>
                </div>
                <div className="wave-metric">
                  <div className="wave-k">Overdue (count)</div>
                  <div className="wave-v">{loading ? '—' : insight.invOverdue.toLocaleString()}</div>
                </div>
                <div className="wave-metric">
                  <div className="wave-k">Active MSAs</div>
                  <div className="wave-v">{loading ? '—' : insight.activeMsas.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dash-recents">
            <div className="dash-card recent-msas">
              <div className="dash-card-head">
                <div className="dash-card-title">Recent MSAs</div>
                <div className="dash-card-meta"><Link to="/msa" className="link">View all</Link></div>
              </div>
              <div className="dash-table">
                <table>
                  <thead><tr><th>MSA ID</th><th>Client</th><th>Status</th></tr></thead>
                  <tbody>
                    {recentMsa.map(m => (
                      <tr key={m.msa_id}>
                        <td><Link to={`/msa/${m.msa_id}`} className="link">{m.msa_id}</Link></td>
                        <td>{m.client_name}</td>
                        <td><span className={`badge badge-${m.status?.toLowerCase()}`}>{m.status}</span></td>
                      </tr>
                    ))}
                    {!recentMsa.length && <tr><td colSpan="3" className="text-center">No MSAs yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dash-card recent-invs">
              <div className="dash-card-head">
                <div className="dash-card-title">Recent Invoices</div>
                <div className="dash-card-meta"><Link to="/invoices" className="link">View all</Link></div>
              </div>
              <div className="dash-table">
                <table>
                  <thead><tr><th>Invoice ID</th><th>Client</th><th>Total</th><th>Status</th></tr></thead>
                  <tbody>
                    {recentInv.map(i => (
                      <tr key={i.invoice_id}>
                        <td><Link to={`/invoices/${i.invoice_id}`} className="link">{i.invoice_id}</Link></td>
                        <td>{i.client_name}</td>
                        <td>{i.invoice_currency} {i.total?.toLocaleString()}</td>
                        <td><span className={`badge badge-${i.status?.toLowerCase()}`}>{i.status}</span></td>
                      </tr>
                    ))}
                    {!recentInv.length && <tr><td colSpan="4" className="text-center">No invoices yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {user?.role === 'Admin' ? (
            <div className="dash-recent-users-row">
              <div className="dash-card recent-users-card">
                <div className="dash-card-head dash-recent-users-head">
                  <div className="dash-card-title dash-recent-users-title">
                    <span className="dash-recent-users-title-icon" aria-hidden>
                      <IconRecentUsersTitle />
                    </span>
                    Recently added users
                  </div>
                  <div className="dash-card-meta">
                    <Link to="/users" className="dash-recent-users-viewall">
                      <span>View all</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>
                </div>
                <div className="dash-table dash-recent-users-table">
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">
                          <span className="dash-ru-th">
                            <IconThId />
                            Username
                          </span>
                        </th>
                        <th scope="col">
                          <span className="dash-ru-th">
                            <IconThUser />
                            Full name
                          </span>
                        </th>
                        <th scope="col">
                          <span className="dash-ru-th">
                            <IconThRole />
                            Role
                          </span>
                        </th>
                        <th scope="col">
                          <span className="dash-ru-th">
                            <IconThStatus />
                            Status
                          </span>
                        </th>
                        <th scope="col">
                          <span className="dash-ru-th">
                            <IconThClock />
                            Added
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {!recentUsersReady ? (
                        <tr>
                          <td colSpan={5} className="dash-ru-empty">
                            <span className="dash-ru-empty-inner">
                              <IconThClock />
                              Loading directory…
                            </span>
                          </td>
                        </tr>
                      ) : !recentUsers.length ? (
                        <tr>
                          <td colSpan={5} className="dash-ru-empty">
                            <span className="dash-ru-empty-inner">
                              <IconRecentUsersTitle />
                              No users found.
                            </span>
                          </td>
                        </tr>
                      ) : (
                        recentUsers.map(u => (
                          <tr key={u.id}>
                            <td className="tabular-nums">
                              <span className="dash-ru-username">{u.username}</span>
                            </td>
                            <td>
                              <div className="dash-ru-namecell">
                                <span className="dash-ru-name-ic" aria-hidden>
                                  <IconThUser />
                                </span>
                                {u.full_name}
                              </div>
                            </td>
                            <td>
                              <span className={`dash-ru-role-pill badge ${dashUserRoleBadgeClass(u.role)}`}>
                                <span className="dash-ru-role-ic" aria-hidden>
                                  <IconRoleGlyph role={u.role} />
                                </span>
                                <span className="dash-ru-role-label">{u.role}</span>
                              </span>
                            </td>
                            <td>
                              <span className={`dash-ru-status ${u.is_active ? 'dash-ru-status--on' : 'dash-ru-status--off'}`}>
                                <span className="dash-ru-status-ic" aria-hidden>
                                  {u.is_active ? <IconActive /> : <IconInactive />}
                                </span>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="tabular-nums">
                              <span className="dash-ru-datecell">
                                <span className="dash-ru-date-ic" aria-hidden>
                                  <IconThClock />
                                </span>
                                <span className="dash-recent-users-date">{formatUserAddedAt(u.created_at)}</span>
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
